import { memo, useEffect, useMemo } from 'react';
import { DentalInsuranceReportTypes } from '@frontend/api-insurance-eligibility-report';
import { theme } from '@frontend/theme';
import { SwitchChipGroup, useSwitchChipGroup } from '@frontend/design-system';
import {
  getUniqueValuesByKey,
  sanitizeString,
} from '../components/InsuranceTables/insurance-reports-table-columns.helper';
import { TablesDataType, TableFilterKey, TableDataType } from '../types/types';

type FilterDetailsType = {
  value: string;
  label: string;
};

type UseInsuranceTableFiltersParams = {
  tableId: keyof TablesDataType;
  tableData: TableDataType[];
  key: TableFilterKey;
  defaultValue?: string;
  filterDetails?: FilterDetailsType[];
  enabled: boolean;
};

type UseInsuranceTableFiltersReturnType = {
  filteredDataByFilterValue: TableDataType[];
  filterValue: string;
  InsuranceTableFilterSwitchComponent: JSX.Element | null;
  uniqueFilterValues: string[];
  filterKey: TableFilterKey | null;
};

export const useInsuranceTableFilters = ({
  defaultValue,
  tableData,
  tableId,
  filterDetails,
  key,
  enabled,
}: UseInsuranceTableFiltersParams): UseInsuranceTableFiltersReturnType => {
  // Get unique filter values from the table data
  const uniqueFilterValues = enabled && key ? getUniqueValuesByKey(tableData, key) : [];

  const filterValueDetails = uniqueFilterValues.map((filterValue) => ({
    label: sanitizeString(filterValue),
    value: filterValue,
  }));

  const selectedStatusProps = useSwitchChipGroup({
    defaultValue: [defaultValue || filterValueDetails?.[0]?.value || ''],
  });

  // Filter the table data based on the selected filter value
  const filteredDataByFilterValue = useMemo(() => {
    return selectedStatusProps?.value?.[0]
      ? tableData.filter((item) => {
          //item is converted record because here item is of tabledatatype which is tuuthfairy type and dentalxchange so filterkey network is not present in tuuthfairy and networkType is not present in dentalxchange
          const value = key in item ? (item as Record<string, string>)?.[key] : '';
          return typeof value === 'string' && value.toLowerCase() === selectedStatusProps?.value?.[0]?.toLowerCase();
        })
      : tableData;
  }, [tableData, selectedStatusProps.value]);

  useEffect(() => {
    if (defaultValue) {
      selectedStatusProps.setValue([defaultValue]);
    }
  }, [defaultValue]);

  /*
   When the global filter is changed tableData and filterValues for local table gets changed 
    so need to update switch chip group value as well below useEffect takes care of it
  */
  useEffect(() => {
    if (!defaultValue) {
      selectedStatusProps.setValue([filterValueDetails[0]?.value]);
    }
  }, [filterValueDetails?.[0]?.value]);
  if (!enabled || !key) {
    return {
      filteredDataByFilterValue: tableData,
      filterValue: '', //As there will be no switch filter no active filtervalue
      InsuranceTableFilterSwitchComponent: null,
      uniqueFilterValues: [],
      filterKey: null, //networkName key is not present in DXC so returning null
    };
  }
  const hasMoreThanOneFilterValue = (filterDetails || filterValueDetails)?.length > 1;

  return {
    filteredDataByFilterValue,
    filterValue: selectedStatusProps?.value?.[0] || '',
    uniqueFilterValues,
    filterKey: key,
    InsuranceTableFilterSwitchComponent: hasMoreThanOneFilterValue ? (
      <InsuranceTableFilterSwitchComponent
        tableId={tableId}
        filterDetails={filterDetails || filterValueDetails}
        selectedStatusProps={selectedStatusProps}
      />
    ) : null,
  };
};

type InsuranceTableFilterSwitchComponentProps = {
  tableId: keyof DentalInsuranceReportTypes.DentalXChangeDentalSummaryInfo;
  selectedStatusProps: ReturnType<typeof useSwitchChipGroup>;
  filterDetails: FilterDetailsType[];
};

const InsuranceTableFilterSwitchComponent = memo(
  ({ tableId, selectedStatusProps, filterDetails }: InsuranceTableFilterSwitchComponentProps) => {
    return (
      <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
        <SwitchChipGroup id={tableId} css={{ margin: theme.spacing(0.5) }} {...selectedStatusProps} singleSelect={true}>
          {filterDetails?.map((filter) => (
            <SwitchChipGroup.Option key={filter.value} value={filter.value}>
              {filter.label}
            </SwitchChipGroup.Option>
          ))}
        </SwitchChipGroup>
      </div>
    );
  }
);

InsuranceTableFilterSwitchComponent.displayName = 'InsuranceTableFilterSwitchComponent';
